
import { Union, Record } from "../fable_modules/fable-library-js.5.3.0/Types.js";
import { string_type, list_type, int32_type, union_type, record_type, bool_type, class_type } from "../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { fromHours } from "../fable_modules/fable-library-js.5.3.0/TimeSpan.js";
import { equals, compare, addDays } from "../fable_modules/fable-library-js.5.3.0/Date.js";
import { localDay } from "./ZonedDate.js";
import { contains, tryFind, empty, exists, max, isEmpty, filter, map } from "../fable_modules/fable-library-js.5.3.0/List.js";
import { op_Subtraction } from "../fable_modules/fable-library-js.5.3.0/DateOffset.js";
import { op_UnaryNegation_Int32 } from "../fable_modules/fable-library-js.5.3.0/Int32.js";
import { toList } from "../fable_modules/fable-library-js.5.3.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.3.0/Range.js";
import { dayNumber } from "../fable_modules/fable-library-js.5.3.0/DateOnly.js";
import { dateHash } from "../fable_modules/fable-library-js.5.3.0/Util.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.3.0/Result.js";
import { equals as equals_1 } from "../fable_modules/fable-library-js.5.3.0/Decimal.js";

/**
 * One logged day, as the rules need it. Also the wire type of GetHostDailyLogsResponse.LoggedDays.
 */
export class LoggedDay extends Record {
    constructor(Date$, LoggedAt, IsHidden) {
        super();
        this.Date = Date$;
        this.LoggedAt = LoggedAt;
        this.IsHidden = IsHidden;
    }
}

export function LoggedDay_$reflection() {
    return record_type("Shared.DailyLogEditWindow.LoggedDay", [], LoggedDay, () => [["Date", class_type("System.DateOnly")], ["LoggedAt", class_type("System.DateTimeOffset")], ["IsHidden", bool_type]]);
}

/**
 * Which logging rules apply. Each caller maps its own status representation onto this:
 * the aggregate from its fold predicates, the client and server from the status string.
 */
export class LogPhase extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Logging", "Finalizing", "Closed"];
    }
}

export function LogPhase_$reflection() {
    return union_type("Shared.DailyLogEditWindow.LogPhase", [], LogPhase, () => [[], [], []]);
}

export class Context extends Record {
    constructor(StartDay, DurationDays, Phase, LoggedDays, Now, Timezone) {
        super();
        this.StartDay = StartDay;
        this.DurationDays = (DurationDays | 0);
        this.Phase = Phase;
        this.LoggedDays = LoggedDays;
        this.Now = Now;
        this.Timezone = Timezone;
    }
}

export function Context_$reflection() {
    return record_type("Shared.DailyLogEditWindow.Context", [], Context, () => [["StartDay", class_type("System.DateOnly")], ["DurationDays", int32_type], ["Phase", LogPhase_$reflection()], ["LoggedDays", list_type(LoggedDay_$reflection())], ["Now", class_type("System.DateTimeOffset")], ["Timezone", string_type]]);
}

export class DayPermission extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["CanAdd", "CanEditAll", "CanEditTextAndPhoto", "ReadOnly"];
    }
}

export function DayPermission_$reflection() {
    return union_type("Shared.DailyLogEditWindow.DayPermission", [], DayPermission, () => [[], [], [], []]);
}

export const sessionWindow = fromHours(4);

export const unitsLockedMessage = "Units can\'t be changed after 2 days because they count toward your total and pledges.";

export function phaseOfStatus(status) {
    switch (status) {
        case "active":
            return new LogPhase(0, []);
        case "finalizing":
            return new LogPhase(1, []);
        default:
            return new LogPhase(2, []);
    }
}

export function lastDay(ctx) {
    return addDays(ctx.StartDay, ctx.DurationDays - 1);
}

/**
 * None when the phase is Closed. Both writable phases clamp the real local today to the last
 * day: while Active the last day can pass before the Watchdog's EndChallenge lands, and that
 * lag must not open future dates.
 */
export function effectiveToday(ctx) {
    const matchValue = ctx.Phase;
    switch (matchValue.tag) {
        case 0:
        case 1: {
            const realToday = localDay(ctx.Timezone, ctx.Now);
            const last = lastDay(ctx);
            return (compare(realToday, last) < 0) ? realToday : last;
        }
        default:
            return undefined;
    }
}

function isInPeriod(ctx, date) {
    if (compare(date, ctx.StartDay) >= 0) {
        return compare(date, lastDay(ctx)) <= 0;
    }
    else {
        return false;
    }
}

/**
 * Effective today and the day before it (both within the period).
 */
export function isFullEditDay(ctx, date) {
    const matchValue = effectiveToday(ctx);
    if (matchValue != null) {
        const today = matchValue;
        if (isInPeriod(ctx, date)) {
            if (equals(date, today)) {
                return true;
            }
            else {
                return equals(date, addDays(today, -1));
            }
        }
        else {
            return false;
        }
    }
    else {
        return false;
    }
}

function anchor(ctx) {
    const _arg = map((l_1) => l_1.Date, filter((l) => (op_Subtraction(ctx.Now, l.LoggedAt) > sessionWindow), ctx.LoggedDays));
    if (isEmpty(_arg)) {
        return addDays(ctx.StartDay, -1);
    }
    else {
        return max(_arg, {
            Compare: (x, y) => (compare(x, y) | 0),
        });
    }
}

function isLogged(ctx, date) {
    return exists((l) => equals(l.Date, date), ctx.LoggedDays);
}

/**
 * Missing days, newest first: (anchor, effectiveToday] within the period, plus the missing
 * full-edit days. Hidden logged days count as logged.
 */
export function fillableDays(ctx) {
    const matchValue = effectiveToday(ctx);
    if (matchValue != null) {
        const today = matchValue;
        const anchorDay = anchor(ctx);
        return filter((d_1) => {
            if (compare(d_1, anchorDay) > 0) {
                return true;
            }
            else {
                return isFullEditDay(ctx, d_1);
            }
        }, filter((d) => {
            if (isInPeriod(ctx, d)) {
                return !isLogged(ctx, d);
            }
            else {
                return false;
            }
        }, map((back) => addDays(today, op_UnaryNegation_Int32(back)), toList(rangeDouble(0, 1, ((dayNumber(today) - dayNumber(ctx.StartDay)) + 1) - 1)))));
    }
    else {
        return empty();
    }
}

export function permission(ctx, date) {
    const matchValue = effectiveToday(ctx);
    if (matchValue != null) {
        const today = matchValue;
        const matchValue_1 = tryFind((l) => equals(l.Date, date), ctx.LoggedDays);
        if (matchValue_1 == null) {
            if (contains(date, fillableDays(ctx), {
                Equals: equals,
                GetHashCode: (x) => (dateHash(x) | 0),
            })) {
                return new DayPermission(0, []);
            }
            else {
                return new DayPermission(3, []);
            }
        }
        else if (matchValue_1.IsHidden) {
            const logged_1 = matchValue_1;
            return new DayPermission(3, []);
        }
        else if (!isInPeriod(ctx, date) ? true : (compare(date, today) > 0)) {
            return new DayPermission(3, []);
        }
        else if (isFullEditDay(ctx, date)) {
            return new DayPermission(1, []);
        }
        else {
            return new DayPermission(2, []);
        }
    }
    else {
        return new DayPermission(3, []);
    }
}

/**
 * Date rules for an add. The caller still checks DayAlreadyLogged and phase separately
 * (typed aggregate errors).
 */
export function validateAdd(ctx, date) {
    const matchValue = effectiveToday(ctx);
    if (matchValue != null) {
        if (compare(date, matchValue) > 0) {
            return new FSharpResult$2(1, ["You can\'t log a future date"]);
        }
        else if (!isInPeriod(ctx, date)) {
            return new FSharpResult$2(1, ["Log date must be within the challenge period"]);
        }
        else if (isLogged(ctx, date) ? true : contains(date, fillableDays(ctx), {
            Equals: equals,
            GetHashCode: (x) => (dateHash(x) | 0),
        })) {
            return new FSharpResult$2(0, [undefined]);
        }
        else {
            return new FSharpResult$2(1, ["This day can no longer be filled in"]);
        }
    }
    else {
        return new FSharpResult$2(1, ["This day can no longer be filled in"]);
    }
}

/**
 * Rules for an update of an existing log.
 */
export function validateUpdate(ctx, date, storedUnits, newUnits) {
    if (exists((l) => {
        if (equals(l.Date, date)) {
            return l.IsHidden;
        }
        else {
            return false;
        }
    }, ctx.LoggedDays)) {
        return new FSharpResult$2(1, ["This log was hidden by a moderator and can\'t be edited"]);
    }
    else {
        const matchValue = permission(ctx, date);
        let matchResult;
        switch (matchValue.tag) {
            case 0:
            case 3: {
                matchResult = 3;
                break;
            }
            case 2: {
                if (equals_1(newUnits, storedUnits)) {
                    matchResult = 1;
                }
                else {
                    matchResult = 2;
                }
                break;
            }
            default:
                matchResult = 0;
        }
        switch (matchResult) {
            case 0:
                return new FSharpResult$2(0, [undefined]);
            case 1:
                return new FSharpResult$2(0, [undefined]);
            case 2:
                return new FSharpResult$2(1, [unitsLockedMessage]);
            default:
                return new FSharpResult$2(1, ["This log can no longer be edited"]);
        }
    }
}

//# sourceMappingURL=DailyLogEditWindow.js.map
